import blockNotification from "@/components/block-notification"
import { useEffect, useMemo, useRef } from "react"

type BlockMutationFn = (args: { variables: { input: Record<string, (string | number)[]> } }) => Promise<unknown>

interface UseBlockNotificationProps {
  selected?: string | number | (string | number)[] | null
  config: {
    key: string
    block: BlockMutationFn
    unblock: BlockMutationFn
    reset: () => void
  }
  onSuccess?: () => void
}

export const useBlockNotification = ({ selected, config, onSuccess }: UseBlockNotificationProps): void => {
  const ids = useMemo(() => (selected ? (Array.isArray(selected) ? selected : [selected]).map(Number) : []), [selected])
  const notificationRef = useRef<ReturnType<typeof blockNotification> | null>(null)

  useEffect(() => {
    if (!ids.length) return

    if (!notificationRef.current) {
      notificationRef.current = blockNotification({
        itemsCount: 0,
        block: () => undefined,
        unblock: () => undefined,
        unselect: () => undefined,
      })
    }

    return () => {
      notificationRef.current?.close()
      notificationRef.current = null
    }
  }, [ids.length])

  useEffect(() => {
    notificationRef.current?.update({
      itemsCount: ids.length,
      block: async () => {
        await config.block({
          variables: { input: { [config.key]: ids } },
        })

        config.reset()
        onSuccess?.()
      },
      unblock: async () => {
        await config.unblock({
          variables: { input: { [config.key]: ids } },
        })

        config.reset()
        onSuccess?.()
      },
      unselect: config.reset,
    })
  }, [ids, config, onSuccess])
}
